import { useState, useRef } from "react";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Copy } from "lucide-react";
import api, { errMsg } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";

const EXPORTS = [
  { key: "customers", label: "Customers" }, { key: "products", label: "Products" },
  { key: "oas", label: "OAs" }, { key: "po", label: "POs" },
  { key: "annexures", label: "Annexures" }, { key: "quotations", label: "Quotations" },
  { key: "bills", label: "Bills" }, { key: "employees", label: "Employees (Admin)", adminOnly: true },
];
const IMPORTS = [
  { key: "customers", label: "Customers" }, { key: "products", label: "Products" },
];

function download(url, params) {
  return api.get(url, { params, responseType: "blob" }).then(({ data, headers }) => {
    const cd = headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const name = m ? m[1] : "export";
    const link = document.createElement("a");
    link.href = URL.createObjectURL(data); link.download = name; link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 60000);
  });
}

export default function ImportExport() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const perms = user.permissions || [];
  const can = (p) => isAdmin || perms.includes(p);
  const [entity, setEntity] = useState("customers");
  const [preview, setPreview] = useState(null);
  const [onDuplicate, setOnDuplicate] = useState("skip");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const doExport = async (ent, fmt) => {
    try { await download(`/export/${ent}`, { format: fmt }); toast.success(`Exported ${ent} (${fmt})`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const doPreview = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/import/${entity}/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/import/${entity}/commit`, { rows: preview.preview, onDuplicate });
      toast.success(`Imported ${data.imported}, skipped ${data.skipped}, replaced ${data.replaced}, errors ${data.errors}`);
      setPreview(null); if (fileRef.current) fileRef.current.value = "";
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader title="Import / Export" subtitle="Bulk data management" />
      <div className="p-6 lg:p-8 space-y-6">
        {can("export_data") && (
          <div className="card p-6">
            <h3 className="font-head font-bold text-lg mb-4 flex items-center gap-2"><Download className="h-5 w-5 text-amber-600" /> Export Data</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {EXPORTS.filter((e) => !e.adminOnly || isAdmin).map((e) => (
                <div key={e.key} className="border border-slate-200 rounded-lg p-3 flex items-center justify-between">
                  <span className="font-medium text-slate-700">{e.label}</span>
                  <div className="flex gap-1">
                    <button className="btn-ghost btn-sm" onClick={() => doExport(e.key, "excel")} data-testid={`export-${e.key}-excel`}>Excel</button>
                    <button className="btn-ghost btn-sm" onClick={() => doExport(e.key, "csv")} data-testid={`export-${e.key}-csv`}>CSV</button>
                    <button className="btn-ghost btn-sm" onClick={() => doExport(e.key, "pdf")} data-testid={`export-${e.key}-pdf`}>PDF</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {can("import_data") && (
          <div className="card p-6">
            <h3 className="font-head font-bold text-lg mb-4 flex items-center gap-2"><Upload className="h-5 w-5 text-amber-600" /> Import Data</h3>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <select className="input w-48" value={entity} onChange={(e) => { setEntity(e.target.value); setPreview(null); }} data-testid="import-entity">
                {IMPORTS.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
              </select>
              <input ref={fileRef} type="file" accept=".csv,.xls,.xlsx" className="input flex-1 min-w-[220px]" onChange={(e) => doPreview(e.target.files[0])} data-testid="import-file" />
              <span className="text-xs text-slate-400 flex items-center gap-1"><FileSpreadsheet className="h-4 w-4" /> CSV / XLS / XLSX</span>
            </div>

            {preview && (
              <div className="space-y-3" data-testid="import-preview">
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="flex items-center gap-1"><FileSpreadsheet className="h-4 w-4 text-slate-500" /> Total: <b>{preview.total}</b></span>
                  <span className="flex items-center gap-1 text-green-700"><CheckCircle2 className="h-4 w-4" /> Valid: <b>{preview.validCount}</b></span>
                  <span className="flex items-center gap-1 text-amber-700"><Copy className="h-4 w-4" /> Duplicates: <b>{preview.duplicateCount}</b></span>
                  <span className="flex items-center gap-1 text-red-700"><AlertTriangle className="h-4 w-4" /> Errors: <b>{preview.errorCount}</b></span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-600">On duplicate:</span>
                  <select className="input w-40" value={onDuplicate} onChange={(e) => setOnDuplicate(e.target.value)} data-testid="import-on-duplicate">
                    <option value="skip">Skip</option><option value="replace">Replace</option>
                  </select>
                </div>
                <div className="border border-slate-200 rounded-lg max-h-80 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0"><tr><th className="th">Row</th><th className="th">Data</th><th className="th">Status</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {preview.preview.map((p, i) => (
                        <tr key={i} className={p.errors.length ? "bg-red-50" : p.duplicate ? "bg-amber-50" : ""}>
                          <td className="td">{p.row}</td>
                          <td className="td text-xs">{Object.values(p.data).filter(Boolean).slice(0, 4).join(" · ")}</td>
                          <td className="td text-xs">{p.errors.length ? <span className="text-red-700">{p.errors.join(", ")}</span> : p.duplicate ? <span className="text-amber-700">Duplicate</span> : <span className="text-green-700">OK</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end"><button className="btn-primary" onClick={commit} disabled={busy} data-testid="import-commit-btn">{busy ? "Importing…" : "Confirm Import"}</button></div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
